import { useGetFeatureToggleVariant } from '@seek/hirer-feature-toggle';
import { useTranslations } from '@vocab/react';
import { Stack, useToast } from 'braid-design-system';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BackButtonPayAndPost } from 'src/app/shared/components/BackButtonPayAndPost';
import { FEATURE_TOGGLE_CART_EDIT_FLOW } from 'src/app/shared/config/features';
import { ROUTE_EDIT_SUCCESS } from 'src/app/shared/constants/routes';
import { actionUpdateIsJobPosted } from 'src/app/v1/state/modules/session/isJobPosted';
import { actionUpdateIsPaymentInProgress } from 'src/app/v1/state/modules/session/isPaymentInProgress';
import { useDraft } from 'src/app/v1/views/hooks/useDraft';
import { useErrorType } from 'src/app/v1/views/hooks/useErrorType';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import { BasicPageLayout } from 'src/app/v1/views/layouts/BasicPageLayout';
import { OldCheckoutSection } from 'src/app/v1/views/pages/EditPayAndPost/sections/OldCheckoutSection';
import { CheckoutSection } from 'src/app/v1/views/pages/EditPayAndPost/sections/checkout';
import { PageSection } from 'src/app/v1/views/sections/PageSection';
import { BenefitList } from 'src/app/v1/views/shared/BenefitList';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';

export const EditPayAndPostPage = () => {
  const { t } = useTranslations(translations);
  const { hidePrice } = useSelector(
    (state: AppState) => ({
      hidePrice: state.session.hirerAccount.hidePrice,
    }),
    shallowEqual,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { saveDraft } = useDraft();
  const showToast = useToast();

  const { goToRoute } = useLocalisedRoutes();

  const isCartEditFlowFeatureEnabled = useGetFeatureToggleVariant(
    FEATURE_TOGGLE_CART_EDIT_FLOW,
  )?.value;

  const handledCancelPayment = () => {
    dispatch(actionUpdateIsPaymentInProgress(false));
  };

  const { errorType, setError, resetError } = useErrorType();

  const onSuccess = async () => {
    await saveDraft(true);
    dispatch(actionUpdateIsJobPosted(true));

    goToRoute(ROUTE_EDIT_SUCCESS);

    showToast({
      message: t('edit-pay-post-page-publish-message'),
      tone: 'positive',
    });
  };

  return (
    <BasicPageLayout
      title={t('edit-pay-post-page-title')}
      errorType={errorType}
    >
      <Stack space="large">
        <BackButtonPayAndPost
          onClick={() => {
            navigate(-1);
          }}
        >
          {t('edit-pay-post-page-back-link')}
        </BackButtonPayAndPost>

        {hidePrice && (
          <PageSection
            heading={t('edit-pay-post-page-benefit-section-heading')}
          >
            <BenefitList
              items={[
                t('edit-pay-post-page-benefit-item-1'),
                t('edit-pay-post-page-benefit-item-2'),
              ]}
            />
          </PageSection>
        )}

        {isCartEditFlowFeatureEnabled ? (
          <CheckoutSection
            resetError={resetError}
            setError={setError}
            onPaymentSuccess={onSuccess}
            handleCancelledPayment={handledCancelPayment}
          />
        ) : (
          <OldCheckoutSection
            resetError={resetError}
            setError={setError}
            onPaymentSuccess={onSuccess}
            handledCancelPayment={handledCancelPayment}
          />
        )}
      </Stack>
    </BasicPageLayout>
  );
};
